import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { TextField, InputAdornment } from '@mui/material'
import useDebounce from 'utils/customHook/useDebounce'
import DisplayedValue from './DisplayedValue'
import { isNil } from 'lodash'
import { darkBlue, disabledColor, errorColor, secondaryBlue } from 'utils/constants/ColorTheme'

export const INPUT_TYPES = {
    text: 'text',
    password: 'password',
    int: 'int',
    float: 'float',
    separateInt: 'separateInt',
    domain: 'domain',
}

const extractDomain = (input) => {
    if (!input) return ''

    const simpleCheck = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/
    if (simpleCheck.test(input)) {
        return input
    }

    try {
        const urlPattern = /^(?:https?:\/\/)?(?:www\.)?([^/:?#]+)/i
        const match = input.match(urlPattern)
        if (match && match[1]) {
            return match[1].split(':')[0]
        }
    } catch (e) {
        console.error(e)
        return input
    }

    return input
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .replace(/\/.*/, '')
        .split(':')[0]
}

const SimpleInput = ({
    value = '',
    onEnterKeyPress = () => { },
    onChange = () => { },
    readMode = false,
    title = '',
    hideNull = false,
    disabled = false,
    className = '',
    placeholder,
    helperText,
    type = INPUT_TYPES.text,
    min,
    max,
    error = false,
    maxLength,
}) => {
    const [stateValue, setStateValue] = useState(value)

    useEffect(() => {
        setStateValue(value)
    }, [value])

    useDebounce(() => onChange(stateValue), 200, [stateValue])

    const sxStyles = useMemo(() => {
        const isDomain = type === INPUT_TYPES.domain

        return {
            paddingTop: '14px !important',
            position: 'relative',

            '& .MuiInputBase-input.MuiOutlinedInput-input': {
                border: isDomain ? 'none' : (!error ? 'solid grey 1px' : `solid ${errorColor} 1px`),
                height: 26,
                marginBottom: 5,
            },

            '& .MuiInputBase-input.MuiOutlinedInput-input:focus:not([readonly])': {
                borderBottom: isDomain ? 'none' : (!error ? `1px solid ${secondaryBlue}` : `1px solid ${errorColor}`),
                boxShadow: isDomain ? 'none' : (!error ? `0 1px 0 0 ${secondaryBlue}` : `0 1px 0 0 ${errorColor}`),
            },

            '& .MuiOutlinedInput-root': {
                border: isDomain ? (!error ? 'solid grey 1px' : `solid ${errorColor} 1px`) : 'none',
                borderRadius: isDomain ? '5px' : '0',
                paddingLeft: isDomain ? '0.5rem' : '0',
                height: isDomain ? 26 : 'none',

                '&.Mui-focused': {
                    borderBottom: isDomain ? (!error ? `1px solid ${secondaryBlue}` : `1px solid ${errorColor}`) : 'none',
                    boxShadow: isDomain ? (!error ? `0 1px 0 0 ${secondaryBlue}` : `0 1px 0 0 ${errorColor}`) : 'none',
                },

                '& fieldset': {
                    border: 'none',
                    height: '0 !important',
                },
            },

            '& .MuiInputAdornment-root': {
                color: disabledColor,
                height: 26,
                maxHeight: 26,
            },

            '& .MuiInputLabel-outlined': {
                top: '-20px',
                color: disabled ? disabledColor : darkBlue,
                fontWeight: 'bold',
                left: '2px',
                fontSize: '1rem',
            },

            '& .MuiInputLabel-outlined.Mui-focused': {
                color: !error ? secondaryBlue : errorColor,
            },

            '& .MuiInputLabel-outlined.Mui-disabled': {
                color: 'grey',
            },
        }
    }, [type, error, disabled])

    if (readMode) {
        return (
            <DisplayedValue
                label={title}
                value={stateValue}
                hideNull={hideNull}
                displayValueBelow
                className={className}
            // obligatory={obligatory}
            />
        )
    }

    const formatMinMax = v => {
        if (v === '-' || v.endsWith('.') || v.endsWith(',')) {
            return v
        }
        const parseV = type === INPUT_TYPES.int ? parseInt(v) : parseFloat(v)
        const vMin = isNil(min) ? parseV : Math.max(parseV, min)
        const vMax = isNil(max) ? vMin : Math.min(vMin, max)
        return `${vMax}`
    }

    const handleChange = newValue => {
        if (newValue === '') {
            setStateValue('')
            return
        }
        if (type === INPUT_TYPES.int) {
            const regex = /^-?\d*$/
            if (regex.test(newValue)) {
                setStateValue(formatMinMax(newValue))
            }
            return
        }
        if (type === INPUT_TYPES.float) {
            const regex = /^-?\d*(\.\d*)?$/
            if (regex.test(newValue)) {
                setStateValue(formatMinMax(newValue))
            }
            return
        }
        if (type === INPUT_TYPES.separateInt) {
            const regex = /^[0-9.,-]+$/ // TODO A améliorer
            if (regex.test(newValue)) {
                setStateValue(formatMinMax(newValue))
            }
            return
        }
        if (type === INPUT_TYPES.domain) {
            if (newValue.includes('/') || newValue.includes(':')) {
                return
            }
            setStateValue(newValue)
            return
        }
        setStateValue(newValue)
    }

    const handlePaste = (e) => {
        if (type === INPUT_TYPES.domain) {
            e.preventDefault()
            const pastedText = e.clipboardData.getData('text')
            const cleanDomain = extractDomain(pastedText)
            setStateValue(cleanDomain)
        }
    }

    return (
        <TextField
            value={stateValue}
            onChange={e => handleChange(e.target.value)}
            onPaste={handlePaste}
            onKeyUp={(event) => {
                if (event.key == 'Enter') {
                    onEnterKeyPress()
                }
            }}
            error={error}
            label={title}
            fullWidth
            InputLabelProps={{
                shrink: false,
            }}
            slotProps={{
                input: {
                    startAdornment: type === INPUT_TYPES.domain ? (
                        <InputAdornment position='start'>https://</InputAdornment>
                    ) : undefined,
                    inputProps: {
                        inputMode: [INPUT_TYPES.int, INPUT_TYPES.float].includes(type) ? 'numeric' : undefined,
                        maxLength,
                    },
                },
            }}
            disabled={disabled}
            sx={sxStyles}
            className={className}
            placeholder={placeholder}
            helperText={helperText}
        />
    )
}

SimpleInput.propTypes = {
    value: PropTypes.string,
    onEnterKeyPress: PropTypes.func,
    onChange: PropTypes.func,
    readMode: PropTypes.bool,
    title: PropTypes.string,
    hideNull: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    type: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    error: PropTypes.bool,
    maxLength: PropTypes.number,
}

export default SimpleInput